import {
  Activity,
  ArrowDownToLine,
  ArrowLeftRight,
  Boxes,
  Briefcase,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CalendarRange,
  ClipboardCheck,
  ClipboardList,
  Cpu,
  FileInput,
  FileUser,
  FileText,
  FolderLock,
  FolderTree,
  Gavel,
  Gauge,
  GitBranch,
  GraduationCap,
  Inbox,
  Layers,
  Mailbox,
  Megaphone,
  Mic,
  ListChecks,
  Package,
  PackageCheck,
  PackagePlus,
  Paperclip,
  Scale,
  ScrollText,
  ShoppingCart,
  Settings,
  Shield,
  ShieldCheck,
  Network,
  Target,
  Truck,
  UserMinus,
  UserRound,
  UserSearch,
  Users,
  Vote,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

/**
 * One entry in the sidebar tree — reference §6.
 *
 * A node is either a **leaf** (`to` set, navigates) or a **group** (`children` set,
 * expands). Groups nest to three levels; deeper than that the indentation stops being
 * readable and the tree should be split into another suite instead.
 */
export interface NavNode {
  /** Stable key. Also the localStorage key for a group's expanded state. */
  key: string;
  label: string;
  icon?: LucideIcon;

  /** Set on leaves only. */
  to?: string;

  /** Hides the whole node, and its subtree, when the caller lacks it. */
  permission?: string;

  /**
   * Hides the node unless the caller holds one of these — for an entry pointing at a
   * CONTAINER of surfaces from different modules, where no single key is true of all of it.
   * `permission` wins when both are set.
   */
  anyPermission?: string[];
  children?: NavNode[];

  /**
   * A destination of a different KIND — configuration rather than work.
   *
   * The entries above it are places to do the job; this is where the vocabulary that job draws
   * on is maintained. The rule above it already says those are two different things, and the
   * rest tone now says it too: a step lighter at rest, so a reader scanning for somewhere to
   * work reads past it rather than over it.
   *
   * **A flag rather than a match on the label.** `divider: true` is nearly this set and not
   * exactly — Approvals' Routing carries one too — and matching `label === 'Settings'` would
   * be a treatment that silently changes when somebody renames an entry. It says what the
   * entry IS; the tone is what this file's reader gets to decide.
   */
  secondary?: boolean;

  /**
   * Draws a horizontal rule above this node.
   *
   * For a group whose entries are not all the same KIND of destination — Organization's
   * Preview, Manage and Plantilla are places to do the work, and Settings is where the
   * vocabulary that work draws on is maintained. The rule says so without a second heading.
   *
   * It is suppressed when the node is first in its list, so a permission gate that hides
   * everything above it cannot leave a rule floating at the top of the group.
   */
  divider?: boolean;

  /**
   * Marks a node as an unwired demonstration entry. It renders in the tree but does not
   * navigate, so a sample can never be mistaken for a working route.
   */
  sample?: boolean;
}

export interface NavSection {
  suite: string;
  items: NavNode[];
}

/**
 * Permission gating for a node and its whole subtree.
 *
 * A group whose every descendant is hidden disappears rather than expanding into nothing.
 * Both the rail and the collapsed flyout resolve visibility through here, so the two can
 * never disagree about what a user is allowed to see.
 */
export function isNavNodeVisible(
  node: NavNode,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (node.permission && !hasPermission(node.permission)) {
    return false;
  }
  if (!node.permission && node.anyPermission && !node.anyPermission.some(hasPermission)) {
    return false;
  }
  if (node.children) {
    return node.children.some((child) => isNavNodeVisible(child, hasPermission));
  }
  return true;
}

/**
 * Whether a section has anything in it for this reader.
 *
 * A suite header over nothing is worse than no header: it tells the reader a section exists
 * and then shows them an empty space where its entries should be, which reads as the nav
 * having failed to load rather than as a permission they lack.
 */
/**
 * The one treatment for a heading that names a group of nav rows.
 *
 * Three surfaces draw one: the expanded rail's suite heading, the flyout's own title, and the
 * flyout's inner group labels. The rail and the flyout are the SAME navigation at two widths,
 * so a reader collapsing the sidebar must not meet a different kind of label — the flyout's
 * title was `text-sm font-medium text-slate-800` and read as a page heading over a menu while
 * the rail beside it used this.
 *
 * Exported rather than retyped for the reason `TOOLBAR_SEARCH_WIDTH` is: the second copy of a
 * literal is where the two stopped matching, and there are three here.
 *
 * Padding stays at the call site — each surface insets differently.
 */
export const NAV_GROUP_LABEL = 'text-[9px] font-semibold uppercase tracking-wider text-gray-600';

export function isNavSectionVisible(
  section: NavSection,
  hasPermission: (permission: string) => boolean,
): boolean {
  return section.items.some((item) => isNavNodeVisible(item, hasPermission));
}

/**
 * Where to send someone who has just signed in.
 *
 * **Derived from this file, never hard-coded.** The landing route used to be a single
 * `hasPermission('users.read')` check, so every account without that key — including one
 * granted `personnel.read` and nothing else — was told it had access to no modules at all,
 * while the sidebar beside it listed the module it could open. One list of modules, one
 * answer about what a reader can reach.
 *
 * Returns null only when the reader genuinely has nowhere to go, which is the one case the
 * "no access yet" message is true.
 */
export function firstVisibleRoute(
  hasPermission: (permission: string) => boolean,
): string | null {
  function walk(nodes: NavNode[]): string | null {
    for (const node of nodes) {
      if (!isNavNodeVisible(node, hasPermission)) continue;

      // A sample node navigates nowhere — landing on one would be a dead end.
      if (node.sample) continue;

      if (node.to) return node.to;

      if (node.children) {
        const nested = walk(node.children);
        if (nested) return nested;
      }
    }

    return null;
  }

  for (const section of NAV_SECTIONS) {
    const route = walk(section.items);
    if (route) return route;
  }

  return null;
}

/** One surface the reader may open, with the group path that names where it lives. */
export interface NavSearchHit {
  node: NavNode;
  /** The labels above it, outermost first — `Organization › Settings`. Empty for a top leaf. */
  trail: string[];
}

export interface NavSearchResult {
  suite: string;
  hits: NavSearchHit[];
}

/**
 * The rail's own entries, filtered by a typed query — the sidebar search (26 Oct 2026).
 *
 * **It lives HERE and not in the component**, because this file already answers "what can this
 * account reach" and the rule is that anything else needing that answer ASKS it rather than
 * deriving a second one. A search that walked its own copy of the tree would be a second list
 * to keep in step, and the one that drifts is always the one nobody is looking at.
 *
 * **Permission is `isNavNodeVisible`, unchanged and non-negotiable.** It is checked on every
 * node on the way DOWN, so a leaf inside a gated group is unreachable even when its own label
 * matches — the group's gate covers its whole subtree, exactly as the rendered rail treats it.
 * A reader can never be shown a destination they would be refused at.
 *
 * **A `sample` node is skipped**, for the reason `firstVisibleRoute` skips one: it navigates
 * nowhere, so offering it as a result is offering a dead end.
 *
 * **A GROUP's label matches on behalf of its children.** Typing `organization` should find the
 * surfaces inside Organization, not nothing — the reader is naming where they want to go, and
 * the group is the word they know it by. The hit is always the LEAF, because only a leaf has a
 * route; the group's name travels in `trail` so the result says where it sits.
 */
export function searchNav(
  query: string,
  hasPermission: (permission: string) => boolean,
): NavSearchResult[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];

  function walk(nodes: NavNode[], trail: string[], parentMatched: boolean): NavSearchHit[] {
    const hits: NavSearchHit[] = [];

    for (const node of nodes) {
      if (!isNavNodeVisible(node, hasPermission)) continue;

      const matched = parentMatched || node.label.toLowerCase().includes(needle);

      if (node.children) {
        hits.push(...walk(node.children, [...trail, node.label], matched));
        continue;
      }

      if (node.sample || !node.to) continue;
      if (matched) hits.push({ node, trail });
    }

    return hits;
  }

  const results: NavSearchResult[] = [];

  for (const section of NAV_SECTIONS) {
    /*
      The SUITE name matches too, so `logistics` lists what is in Logistics. It seeds
      `parentMatched` rather than being a special case further down.
    */
    const hits = walk(section.items, [], section.suite.toLowerCase().includes(needle));
    if (hits.length > 0) results.push({ suite: section.suite, hits });
  }

  return results;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    /*
      APPROVALS — a TOP-LEVEL SUITE, not a group inside Administration. The approval inbox is
      daily work for most readers and shares nothing with users, roles and audit logs. The
      routes have always been top-level (`/approvals/…`); only the rail disagreed with them.

      A SECTION TAKES NO `permission` — `NavSection` is `{ suite, items }` and has no field for
      one. Each row gates itself, and `isNavSectionVisible` drops the whole heading when every
      row under it is hidden, which is the same outcome the group's deliberate absence of a key
      used to produce.
    */
    suite: 'Approvals',
    items: [
      {
        /*
          NAMED FOR WHAT IT IS, not for how it is read. "Inbox" describes the reader's
          relationship to the list; `Requests` is what the list holds — the correction
          Personnel's `Preview` → `Data Sheet` → `201 Files` and the org chart's
          `Preview` → `Chart` already made.
        */
        key: 'approval-requests',
        label: 'Requests',
        icon: Inbox,
        to: '/approvals/requests',
        permission: 'approvals.read',
      },
      {
        /*
          LAST and behind a rule: Requests is where the work happens, and this is where the
          routing that work draws on is maintained. A divider is what says those are two
          different kinds of destination.
        */
        key: 'approval-routing',
        label: 'Routing',
        icon: Settings,
        to: '/approvals/routing',
        permission: 'approval-categories.read',
        divider: true,
      },
    ],
  },
  {
    suite: 'Human Resources',
    items: [
      {
        /*
          A group, not a leaf, and its children are of two kinds.

          Preview, Manage and Settings are SURFACES over the module's own records and its
          configuration, so they nest under the module's path.

          Plantilla is a separate MODULE that hangs off the organization: a post exists in a
          unit. It keeps its own top-level route and its own permission key, because it is not
          a view of the chart — it is its own records with their own lifecycle.

          The grouping is navigational, and it is the reason the group itself carries no
          `permission`: `org-units.read` on the group would hide Plantilla from someone
          entitled to read it but not the chart.
        */
        key: 'organization',
        label: 'Organization',
        icon: Network,
        children: [
          {
            /*
              One chart surface, not two — and it is called what it IS. Preview and Manage
              were the same picture with and without a kebab, which made the rail ask the
              reader to choose between looking at the chart and working on it; merged on
              14 Sep 2026. The survivor kept the word Preview, which named how the surface
              was READ rather than what it holds, and became `Chart` on 20 Sep 2026 —
              then `Units/Chart` on 06 Oct 2026, naming the RECORDS as well as the shape
              they are drawn in. The ROUTE stayed `/organization/chart`: a slash is not a
              path segment, and the path still names the surface honestly.
            */
            key: 'organization-chart',
            label: 'Units/Chart',
            icon: GitBranch,
            to: '/organization/chart',
            permission: 'org-units.read',
          },
          {
            key: 'plantilla',
            label: 'Plantilla',
            icon: Briefcase,
            to: '/plantilla',
            permission: 'plantilla.read',
          },
          {
            /*
              Settings is a MENU of the module's configuration, one row per catalog, each
              leading to its own page. It is an extension of this sidebar: the rail keeps one
              entry per module rather than growing a row for every configurable list, which is
              how a sidebar stops being navigation and becomes a list of everything.

              It carries no single key, because its rows do not share one — Tiers is
              `org-unit-types`, Positions is `positions`, Salary Grades is `salary-grades`.
              `org-units.update` was wrong twice over: it hid the page from someone entitled to
              maintain the position catalog, and it is not a grant any row actually needs.

              LAST in the group and behind a rule: the entries above it are places to do the
              work, and this is where the vocabulary that work draws on is maintained. A
              divider is what says those are two different kinds of destination.
            */
            key: 'organization-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/organization/settings',
            anyPermission: ['org-unit-types.read', 'positions.read', 'salary-grades.read'],
            divider: true,
          },
        ],
      },
      {
        /*
          A sibling of Organization, not a child of it — and the distinction is worth stating
          because Plantilla sits inside that group on a similar-sounding argument.

          Plantilla is there because a post exists IN a unit: you read the chart to understand
          the staffing pattern, and the two are looked at together. Personnel is about people,
          and a reader looking for an employee does not start at the organizational chart. The
          module hangs off the plantilla (a person occupies an item) but nesting it under
          Organization would make that group the whole suite, which is how a sidebar stops
          being navigation.
        */
        key: 'personnel',
        label: 'Personnel',
        icon: Users,

        /*
          The GROUP carries NO key, exactly like Organization's, and the reasoning is the same
          one: it holds a sibling module's surface, not only its own.

          It carried `personnel.read` and that was wrong — it hid the whole branch from a reader
          entitled to Settings but not to employment records, which is the failure the Settings
          child's own comment describes and then met one level up. Identification and Family
          Relations were already reachable only through this branch; moving Eligibilities in
          made it plain, because the catalog's reader is very often not a reader of 201 files.

          `isNavNodeVisible` falls through to the children with no key here, so the group shows
          whenever any child does.
        */
        children: [
          {
            /*
              `201 Files`. Product call, 22 Oct 2026, replacing `Data Sheet` — which had itself
              replaced `Preview` on 19 Sep.

              Each step named the thing more exactly. `Preview` named nothing at all, copied
              from the org chart where the word says the chart is read rather than worked on.
              `Data Sheet` named the FORM — the Personal Data Sheet, CS Form 212 — which is one
              document inside the record rather than the record itself, and the product already
              had a separate `ApplicantRequirement` literally called "Personal Data Sheet". This
              surface lists 201 FILES, which is what the domain calls the whole employee record
              and the word `personnel.md` has used throughout.

              The ROUTE moved with it, because a path is the same vocabulary as a label, and BOTH
              old paths redirect rather than 404. The permission KEY did not: a key is an
              identifier, and renaming one is a migration for no gain.
            */
            key: 'personnel-201-files',
            label: '201 Files',
            icon: Users,
            to: '/personnel/201-files',
            permission: 'personnel.read',
          },
          {
            /*
              LAST in the group and behind a rule, like every Settings entry: the row above is
              where the work happens, and this is where the vocabulary that work draws on is
              maintained. Two different kinds of destination, one heading.

              Gated on ANY of its rows' keys rather than on the module's: Identification is its
              own module with its own key, and `personnel.read` on the page would hide it from
              someone entitled to maintain the catalog without reading employment records.
            */
            key: 'personnel-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/personnel/settings',
            anyPermission: [
              'identification-types.read',
              'family-relations.read',
              'eligibilities.read',
              'survey-questions.read',
              'competencies.read',
              'learning-development.read',
              'personnel-document-types.read',
              'personnel.manage-catalogs',
            ],
            divider: true,
          },
        ],
      },
      {
        /*
          RECRUITMENT — a sibling of Organization and Personnel. Vacancies is its
          first module; the group exists for the ones that follow it.

          The group carries NO key, like its two siblings: its children will be separate modules
          with their own keys, and a key here would hide the branch from a reader entitled to one
          of them. And its routes do not nest under it — a group of sibling MODULES groups
          navigation, not paths, the way Plantilla stays at `/plantilla`. Settings is the one path
          that DOES nest (`/recruitment/settings`): the catalog behind it is reached only that way.
        */
        key: 'recruitment',
        label: 'Recruitment',
        icon: FileUser,
        children: [
          {
            key: 'vacancies',
            label: 'Vacancies',
            icon: BriefcaseBusiness,
            to: '/vacancies',
            permission: 'vacancies.read',
          },
          {
            key: 'applicants',
            label: 'Applicants',
            icon: UserSearch,
            to: '/applicants',
            permission: 'applicants.read',
          },
          {
            /*
              Deliberate (20 Oct 2026) — the open postings, and their applicants' scores side by side.
              Read only, on its own key: a view of Applicants' records, below the module it reads.
            */
            key: 'deliberation',
            label: 'Deliberate',
            icon: Vote,
            to: '/deliberation',
            permission: 'deliberations.read',
          },
          {
            /*
              The checklist Initial Screening ticks off (19 Oct 2026) and the Score Points the Summary
              is weighted by (20 Oct 2026). LAST and behind a rule, like Organization's: the entries
              above are where the work is done, this is the vocabulary it draws on. Gated on ANY of
              its rows' keys.
            */
            key: 'recruitment-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/recruitment/settings',
            anyPermission: ['applicant-requirements.read', 'applicants.read'],
            divider: true,
          },
        ],
      },
      {
        /*
          ATTRITION (29 Oct 2026) — separations from the service, filed by HR under a category
          with that category's document checklist. A sibling of Recruitment: one brings people in,
          the other sees them out.

          The group carries NO key: Requests and the categories behind Settings are two modules
          with their own keys, and a key here would hide the branch from a reader entitled to one.
        */
        key: 'attrition',
        label: 'Attrition',
        icon: UserMinus,
        children: [
          {
            key: 'attrition-requests',
            label: 'Requests',
            icon: ClipboardList,
            to: '/attrition/requests',
            permission: 'attrition-requests.read',
          },
          {
            // LAST and behind a rule; gated on ANY of its rows' keys — a row added owes its key here.
            key: 'attrition-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/attrition/settings',
            anyPermission: ['attrition-categories.read'],
            divider: true,
          },
        ],
      },
    ],
  },

  /*
    Document Management.

    Repository is the controlled-document registry AND the container every file in rsys_ is
    filed into. Its key governs the first of those: a folder owned by another module has its
    contents gated on that module's read key instead, which is what stops `repository.read`
    becoming a master key to every byte in the product.

    ROUTING arrived 09 Oct 2026 — Receiving and Dispatching, the records office's log of what
    comes in and goes out. With it the suite has TWO Settings rows, which is exactly what the
    old note here said would need a group per module rather than a longer label: a bare
    "Settings" under the suite heading would not say which it configures. So each is a group,
    and each Settings sits last in its own.
  */
  {
    suite: 'Document Management',
    items: [
      {
        /*
          A GROUP now, because it has two surfaces and a neighbour with Settings of its own. It
          was a leaf while it was the suite's only module — the single-child "Browse" group that
          came before cost two rows for one destination. Its paths are unchanged; one module's
          surfaces, so the group may carry the module's own read.
        */
        key: 'repository',
        label: 'Repository',
        icon: FolderTree,
        permission: 'repository.read',
        children: [
          {
            key: 'repository-documents',
            label: 'Documents',
            icon: FileText,
            to: '/repository',
            permission: 'repository.read',
          },
          {
            key: 'repository-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/repository/settings',
            permission: 'repository.read',
            divider: true,
          },
        ],
      },
      {
        /*
          ROUTING groups three SIBLING MODULES and their shared catalog, so the group carries NO
          `permission` — a key here would hide it from a reader holding only another. It
          groups NAVIGATION, not routes: each module keeps its own path, and the catalog nests
          under its heavier consumer's Settings (Receiving), the one menu it belongs to.
        */
        key: 'routing',
        label: 'Routing',
        icon: GitBranch,
        children: [
          {
            key: 'document-receiving',
            label: 'Receiving',
            icon: Inbox,
            to: '/document-receiving/documents',
            permission: 'document-receiving.read',
          },
          {
            // Where a dispatch to a unit arrives — Receiving is the paper desk, this is the hand-over.
            key: 'document-inbox',
            label: 'Inbox',
            icon: Mailbox,
            to: '/document-inbox',
            permission: 'document-inbox.read',
          },
          {
            key: 'document-dispatching',
            label: 'Dispatching',
            icon: Truck,
            to: '/document-dispatching',
            permission: 'document-dispatching.read',
          },
          {
            key: 'routing-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/document-receiving/settings',
            permission: 'routing-document-types.read',
            divider: true,
          },
        ],
      },
    ],
  },

  /*
    Case Management — the case REGISTRY, parent of every case-related record (product call,
    12 Oct 2026). One leaf: registering a case is a work surface, not a catalog. Holding the key
    opens no case — the case must be shared with the reader, who must be an employee cleared for
    its level.
  */
  {
    suite: 'Case Management',
    items: [
      {
        key: 'cases',
        label: 'Cases',
        icon: Gavel,
        to: '/cases',
        permission: 'cases.read',
      },
    ],
  },

  /*
    Evidence Management — Repository's access model and Inventory's custody ledger, for the
    evidence a case holds. The case is registered in Case Management.

    ONE work surface — product call, 08 Oct 2026: the Evidence module IS the case list, and a case
    opens on its evidence. There is no list of evidence across cases.

    The group carries NO `permission`: Evidence and Settings are separate modules with their own
    keys. A key here would hide the branch from a reader entitled to
    one child and not another. Holding a key opens no case — the case must also be shared with the
    reader, who must be an employee cleared for its level — so the rail says what the account may
    USE, and the lists say what it may SEE.
  */
  {
    suite: 'Evidence Management',
    items: [
      {
        key: 'evidence-management',
        label: 'Cases',
        icon: ShieldCheck,
        children: [
          {
            key: 'evidence-cases',
            label: 'Evidence',
            icon: FolderLock,
            to: '/evidence/cases',
            permission: 'evidence-items.read',
          },
          {
            key: 'evidence-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/evidence/settings',
            anyPermission: [
              'evidence-storage-locations.read',
              'evidence-kinds.read',
              'evidence-types.read',
              'evidence-disposal-types.read',
              'evidence-file-types.read',
            ],
            divider: true,
          },
        ],
      },
    ],
  },

  /*
    Logistics.

    Inventory is the suite's first module GROUP, and it holds surfaces belonging to five
    different modules — Items is its own, and the four catalogs behind Settings are four more.

    The group therefore carries NO `permission`, exactly like Organization's: a key here would
    hide the whole branch from a reader entitled to one of its children but not the others —
    say, somebody who maintains the vocabulary and never touches stock. `isNavNodeVisible`
    falls through to the children with no key, so the group shows whenever any child does.

    **Storage Facilities was a row here and is now a Settings entry.** The test is whether a
    reader goes somewhere to DO something or to change what the doing draws on: stock operations
    draw on a facility, and nothing is done to the facility itself, so it belongs with the
    vocabulary rather than beside Items. If it ever grows a surface showing what is IN it, that
    is work and the row comes back.

    The THREE VOCABULARIES are deliberately not rows here for a second, sharper reason:
    `inventory-items.read` implies all three unscoped, which is only correct while they have no
    sidebar entry for that grant to arrive at. Promoting one would hand every item reader a
    module nobody granted them. That argument does not apply to Storage Facilities — nothing
    implies its read — which is why it could have stayed and moved on the first test alone.
  */
  {
    suite: 'Logistics',
    items: [
      {
        key: 'logistics-inventory',
        label: 'Inventory',
        icon: Boxes,
        children: [
          {
            /*
              MONITORING — the read-only views over the stock records: the roll-up and the ledger
              it is made of. Nothing in either is created, edited or deleted. A group of sibling
              MODULES, so no `permission` on the group — each child keeps its own key. FIRST in
              Inventory by product call (08 Oct 2026), so the group opens on the figures.
            */
            key: 'inventory-monitoring',
            label: 'Monitoring',
            icon: Activity,
            children: [
              {
                /*
                  The ROLL-UP over Stock Batches and Stock Units: a reader opens it to find out what
                  to reorder, which is a thing to do rather than vocabulary to maintain. It is the
                  group's FIRST entry, and /inventory lands here.

                  Its own key, and it is the only one this module has: every figure is computed at
                  read time, so there is nothing here to create, edit or delete.
                */
                key: 'stock-levels',
                label: 'Stock Levels',
                icon: Gauge,
                to: '/inventory/stock-levels',
                permission: 'stock-levels.read',
              },
              {
                /*
                  THE LEDGER — what every stock figure is made of. A work surface rather than a
                  Settings row: a reader comes here to answer where stock went, which is a thing to
                  find out, not a vocabulary to maintain.

                  Its own key, and — like Stock Levels — the only one this module has. Nothing here
                  can be created, edited or deleted: a movement is written by `StockLedger` as part
                  of the operation that moved the stock, and the table is append-only.
                */
                key: 'stock-movements',
                label: 'Stock Movements',
                icon: ArrowLeftRight,
                to: '/inventory/stock-movements',
                permission: 'stock-movements.read',
              },
            ],
          },
          {
            /*
              INGRESS — how stock comes IN and the records it lands as: a delivery taken in at the
              door, and the lots and serialized units it is filed under. Sibling MODULES again,
              so it carries NO `permission` — each child keeps its own key, and the group shows
              whenever any of them does. It groups navigation, not routes: every path stays at
              `/inventory/...`.
            */
            key: 'inventory-ingress',
            label: 'Ingress',
            icon: ArrowDownToLine,
            children: [
              {
                /*
                  The lots a quantity item is counted in. Items moved behind Settings on 18 Sep
                  2026 — see the Settings entry below — because a lot is filed against an item and
                  nothing is done to the item itself.

                  Its own key rather than the group's: `stock-batches` is a module with its own
                  permission set, and a reader may hold it without holding the item catalog's.
                */
                key: 'stock-batches',
                label: 'Stock Batches',
                icon: Boxes,
                to: '/inventory/stock-batches',
                permission: 'stock-batches.read',
              },
              {
                // The other half of the stock model: a quantity item is COUNTED in lots, a
                // serialized one is NAMED one row per thing. Two lists because they answer two
                // different questions, and two permission keys for the same reason.
                key: 'stock-units',
                label: 'Stock Units',
                icon: Cpu,
                to: '/inventory/stock-units',
                permission: 'stock-units.read',
              },
              {
                /*
                  GOODS RECEIPTS — a reader comes here to take a delivery in at the door and verify
                  it against what was ordered. Nothing about it is a vocabulary the doing draws on.

                  `goods-receipts.read` — the register's own key. The order and store pickers behind
                  the wizard carry `create` instead, scoped, because a reader who may only look at the
                  register has no business enumerating the open purchase orders through it.
                */
                key: 'goods-receipts',
                label: 'Goods Receipts',
                icon: PackagePlus,
                to: '/inventory/goods-receipts',
                permission: 'goods-receipts.read',
              },
            ],
          },
          {
            /*
              STOCK LEAVING — dispensed, assigned into custody, or lent out. A work surface, and
              the most obviously one: a reader comes here to hand stock over and to take it back,
              which is the doing rather than the vocabulary the doing draws on.

              `issuances.read` — the list's own key. The picker feeds behind the Issue form carry
              `issuances.create` instead, because they disclose what is in stock and where; a
              reader who may only look at the register has no business enumerating the stores'
              contents through it.
            */
            key: 'issuances',
            label: 'Issuances',
            icon: PackageCheck,
            to: '/inventory/issuances',
            permission: 'issuances.read',
          },
          {
            /*
              PHYSICAL STOCK TAKES — what the system believed, what was found, and the difference.
              A work surface, and unambiguously one: a reader comes here to walk a store with a
              clipboard and write down what is on the shelf. Nothing about it is a vocabulary the
              doing draws on.

              `inventory-audits.read` — the register's own key. The facility and personnel pickers
              behind the forms carry `create` and `record-count` instead, scoped, because a reader
              who may only look at the register has no business enumerating the stores or the
              staff through it.
            */
            key: 'inventory-audits',
            label: 'Inventory Audits',
            icon: ClipboardCheck,
            to: '/inventory/inventory-audits',
            permission: 'inventory-audits.read',
          },
          {
            /*
              EMPLOYEE ASSETS — a work surface. A reader comes to find out what a person holds,
              which is a thing to find out rather than a vocabulary the doing draws on.

              `employee-assets.read` — the module's one key. It is a view over personnel records
              and issuance lines, so nothing here is created, edited or deleted.
            */
            key: 'employee-assets',
            label: 'Employee Assets',
            icon: UserRound,
            to: '/inventory/employee-assets',
            permission: 'employee-assets.read',
          },
          {
            /*
              LAST in the group and behind a rule, like every Settings entry: the rows above are
              where the work happens, and this is where everything that work draws on is
              maintained. Two kinds of destination, one heading.

              **ITEMS is one of its rows, from 18 Sep 2026.** It was the group's first rail entry
              until the stock surfaces landed, and the test that moved it is the one this rule
              already sets: a reader goes to Stock Batches, Stock Units and Stock Levels to DO
              something, and to Items to change what that doing draws on — a lot, a unit and a
              level are each filed against an item, and nothing is done to the item itself.

              Gated on ANY of its five rows' keys rather than on one — each is its own module,
              and naming a single key here would hide the page from somebody entitled to
              maintain a vocabulary without reading the catalog, or the reverse.
            */
            key: 'inventory-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/inventory/settings',
            anyPermission: [
              'inventory-items.read',
              'inventory-categories.read',
              'inventory-item-types.read',
              'units-of-measure.read',
              'storage-facilities.read',
            ],
            divider: true,
          },
        ],
      },
    ],
  },

  /*
    PROCUREMENT — a suite, a sibling of Human Resources and Logistics.

    Investment Planning is its first module GROUP. What it will hold is Budget Plan, Budget
    Review, Allotment Release and Obligations — four work surfaces, in that order, because that
    is the order the budget cycle runs in: a plan is built, reviewed, released against, and
    obligated from. None of them is built yet, so the group opens on Settings.

    **The group carries NO `permission`**, exactly like Organization's and Inventory's: its
    children belong to several modules, and a key here would hide the whole branch from a
    reader entitled to one of them but not the others — say, somebody who maintains the goal
    framework and never touches a fund source. `isNavNodeVisible` falls through to the children
    with no key, so the group shows whenever any child does.

    The three CATALOGS are deliberately not rows here. They are configuration — a reader goes
    to a budget plan to DO something and to these to change what the doing draws on — which is
    the test every Settings row takes. Unlike Inventory's three vocabularies, no implied read
    turns on where they sit: nothing implies their read, so they are here on the
    work-versus-configuration test alone, and could legitimately come back to the rail the day
    a reader can do something to one.
  */
  {
    suite: 'Procurement',
    items: [
      {
        key: 'procurement-investment-planning',
        label: 'Investment Planning',
        icon: Target,
        children: [
          /*
            The group's FIRST WORK SURFACE, and the first of the four. Budget Review, Allotment
            Release and Obligations append after it in the cycle's order — a plan is built,
            reviewed, released against, and obligated from — never slotted in, so a reader who
            has learned where a row sits keeps finding it there.
          */
          {
            key: 'procurement-budget-plans',
            label: 'Budget Plans',
            icon: CalendarRange,
            to: '/procurement/investment-planning/budget-plans',
            permission: 'budget-plans.read',
          },

          /*
            THE SECOND work surface, appended after Budget Plans rather than slotted in — the
            cycle's own order, and the rule every row in this rail follows: a new entry goes
            last among the work surfaces, so the position of one a reader has already learned
            stays where it was. Allotment Release and Obligations append after it.
          */
          {
            key: 'procurement-budget-review',
            label: 'Budget Review',
            icon: Scale,
            to: '/procurement/investment-planning/budget-review',
            permission: 'budget-review.read',
          },

          /*
            LAST and behind a rule, like every Settings entry: the rows above are where the
            work happens, and this is where the vocabulary that work draws on is maintained.
          */
          {
            key: 'procurement-investment-planning-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/procurement/investment-planning/settings',
            anyPermission: [
              'budget-goals.read',
              'budget-account-codes.read',
              'budget-departments.read',
            ],
            divider: true,
          },
        ],
      },

      /*
        THE SUITE'S SECOND MODULE GROUP, appended after Investment Planning rather than slotted
        in: the budget cycle runs first and the procurement plan is written against what it
        approved, which is also the order a reader learns them in.

        It holds ONE entry today and that entry is Settings, because everything built so far is
        a vocabulary a procurement line is written in. The APP and the PPMP itself are work
        surfaces and append ABOVE it when they land — Settings stays last in its group, which
        is the rule every group in this rail follows.

        NO DIVIDER on that row while it is the first visible node: a rule at the top of a group
        separates it from nothing. `<SidebarNav>` suppresses it, so the flag is set here and the
        rail does the right thing both before and after the work surfaces arrive.
      */
      {
        key: 'procurement-planning',
        label: 'Procurement Planning',
        icon: ClipboardList,
        children: [
          /*
            THE GROUP'S FIRST WORK SURFACE, appended ABOVE Settings — a reader goes here to do
            something, where the three catalogs behind Settings are what the doing draws on.
            Settings stays last in its group, with the rule above it, which is what every group
            in this rail does.
          */
          {
            key: 'annual-procurement-plans',
            label: 'Annual Procurement Plan',
            icon: CalendarRange,
            to: '/procurement/procurement-planning/annual-procurement-plans',
            permission: 'annual-procurement-plans.read',
          },
          {
            key: 'procurement-planning-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/procurement/procurement-planning/settings',
            anyPermission: [
              'procurement-catalogs.read',
              'ppmp-items.read',
              'ppmp-departments.read',
            ],
            divider: true,
          },
        ],
      },
      {
        /*
          PURCHASING — the suite's THIRD module group, appended after Investment Planning and
          Procurement Planning rather than slotted in: the budget cycle runs first, the
          procurement plan is written against what it approved, and the buying happens against
          that plan. A new group goes last, so the position of one a reader has already learned
          stays where it was.

          Product call, 29 Sep 2026. The source keeps purchase requests inside its Logistics
          module; here the buying cycle is its own group under Procurement.

          THE GROUP CARRIES NO `permission`, like the two above it: `isNavNodeVisible` falls
          through to the children and shows the group when any one of them is visible, which is
          the correct behaviour once a second module joins it.
        */
        key: 'procurement-purchasing',
        label: 'Purchasing',
        icon: ShoppingCart,
        children: [
          {
            key: 'purchase-requests',
            label: 'Purchase Requests',
            icon: ClipboardList,
            to: '/procurement/purchasing/purchase-requests',
            permission: 'purchase-requests.read',
          },

          /*
            THE GROUP'S SECOND WORK SURFACE, appended after Purchase Requests rather than slotted
            in — the cycle's own order, and the rule every row in this rail follows: a new entry
            goes last among the work surfaces, so the position of one a reader has already
            learned stays where it was. A request is raised, approved, and then ordered against.
          */
          {
            key: 'purchase-orders',
            label: 'Purchase Orders',
            icon: Truck,
            to: '/procurement/purchasing/purchase-orders',
            permission: 'purchase-orders.read',
          },

          /*
            LAST and behind a rule, like every Settings entry: the rows above are where the work
            happens, and this is where the vocabulary that work draws on is maintained. A
            supplier is what an order DRAWS ON — every order form picks one, and nothing is done
            to a supplier itself.
          */
          {
            key: 'purchasing-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/procurement/purchasing/settings',
            anyPermission: ['suppliers.read'],
            divider: true,
          },
        ],
      },
    ],
  },

  /*
    A static, unwired sample showing the three nesting levels the sidebar supports:

      Level 1  Sample Module
        Level 2  Stock Control
          Level 3  Stock Counts

    Nothing here navigates. Delete this section once a real module uses the third level — it
    exists to demonstrate the shape, not to ship.

    **Its label used to be "Inventory" and was renamed when the real one arrived.** Two entries
    called Inventory in one rail is the naming failure this product forbids, and the demo is the
    half that gives way. Its keys are left alone: they are unique against the real module's
    (`logistics-inventory`), and churning them would change nothing a reader can see.
  */
  /*
    GENERAL SERVICES — a top-level suite beside Human Resources, product call 07 Oct 2026.
    Appended after Procurement rather than slotted in, so the position of every suite a reader
    has already learned stays where it was; Self Service stays last on its own argument.

    BUILDING MANAGEMENT is ONE module with several surfaces, so it is a GROUP: the monitor, the
    calendar over the same rows, and Settings behind a rule. The group carries NO `permission` —
    Settings holds the category catalog, a sibling module with its own keys, and a key here
    would hide that row from a reader entitled to it.
  */
  {
    suite: 'General Services',
    items: [
      {
        key: 'building-management',
        label: 'Building Management',
        icon: Building2,
        children: [
          {
            key: 'building-management-maintenance',
            label: 'Maintenance',
            icon: Wrench,
            to: '/general-services/building-management/maintenance',
            permission: 'building-management.read',
          },
          {
            key: 'building-management-calendar',
            label: 'Calendar',
            icon: CalendarDays,
            to: '/general-services/building-management/calendar',
            permission: 'building-management.read',
          },
          {
            // ANY of its rows' keys — the catalog's, and the module's own read for Aging.
            key: 'building-management-settings',
            label: 'Settings',
            secondary: true,
            icon: Settings,
            to: '/general-services/building-management/settings',
            anyPermission: ['maintenance-categories.read', 'building-management.read'],
            divider: true,
          },
        ],
      },
    ],
  },

  /*
    LEARNING & DEVELOPMENT — a top-level suite beside Human Resources. Appended after General
    Services rather than slotted in, so the position of every suite a reader has already
    learned stays where it was; Self Service stays last on its own argument.

    SURVEY, DEVELOPMENT PLAN and TRAINING RECOMMENDATIONS are sibling modules with their own
    keys, each a LEAF — the rail goes straight to the list. Settings sits at suite level behind a
    rule because it is a different KIND of destination; it is Survey's, since neither register
    has anything to configure. No path nests under `/learning-development/survey`: the rail draws its active
    bar by prefix, and a nested entry would light the Survey row too.

    When a second module arrives with settings of its OWN, this becomes a group per module —
    two bare "Settings" rows under one heading would not say which they configure.
  */
  {
    suite: 'Learning & Development',
    items: [
      {
        key: 'learning-development-survey',
        label: 'Survey',
        icon: ListChecks,
        to: '/learning-development/survey',
        permission: 'surveys.read',
      },
      {
        // Every assessed employee's plan, across every survey — its own module and its own read.
        // Its path sits beside Survey's, not under it, for the prefix reason above.
        key: 'learning-development-development-plan',
        label: 'Development Plan',
        icon: FileText,
        to: '/learning-development/development-plan',
        permission: 'development-plans.read',
      },
      {
        // Every plan's training recommendations, across every survey — its own module and its
        // own read, as Development Plan is.
        key: 'learning-development-training-recommendation',
        label: 'Training Recommendations',
        icon: GraduationCap,
        to: '/learning-development/training-recommendation',
        permission: 'training-recommendations.read',
      },
      {
        // Survey's own read while it has no rows of its own; a row added owes its key here
        // as `anyPermission`, and to the route, in the same change.
        key: 'learning-development-settings',
        label: 'Settings',
        secondary: true,
        icon: Settings,
        to: '/learning-development/settings',
        permission: 'surveys.read',
        divider: true,
      },
    ],
  },

  /*
    PUBLIC RELATIONS — a top-level suite, product call 12 Oct 2026. Appended after Learning &
    Development rather than slotted in, so the position of every suite a reader has already
    learned stays where it was; Self Service stays last on its own argument.

    PRESS RELEASE is a LEAF — the rail goes straight to the register. It REPLACED Post Operation
    Report on 12 Oct 2026. PRESS CON (13 Oct 2026) is a second leaf beside it, on its own key. SETTINGS (its Channels catalog) sits at suite level behind a rule, the
    Learning & Development arrangement: nested under the leaf's path, the rail would light both.
  */
  {
    suite: 'Public Relations',
    items: [
      {
        key: 'press-releases',
        label: 'Press Release',
        icon: Megaphone,
        to: '/public-relations/press-releases',
        permission: 'press-releases.read',
      },
      {
        // A second LEAF (13 Oct 2026) — its own module and key, so no group over the two.
        key: 'press-conferences',
        label: 'Press Con',
        icon: Mic,
        to: '/public-relations/press-conferences',
        permission: 'press-conferences.read',
      },
      {
        // Its one row's key, as Survey's Settings is; a row added owes its key here as `anyPermission`.
        key: 'public-relations-settings',
        label: 'Settings',
        secondary: true,
        icon: Settings,
        to: '/public-relations/settings',
        permission: 'press-release-channels.read',
        divider: true,
      },
    ],
  },

  /*
    CASH DIVISION — its own suite. Requests for a disbursement run Pending → At
    Accounting → For release → Disbursed, every step a Cash Division act: Accounting is not a
    user of this system, so a request at Accounting is only tagged.

    Placed before Self Service, which stays LAST among the real suites (`firstVisibleRoute`
    walks these in order).

    No group node: the suite holds two leaves directly, the way Administration does. Requests is
    the work surface; Settings is last, behind a rule, and gated on ANY of its rows' keys.
  */
  {
    suite: 'Cash Division',
    items: [
      {
        key: 'disbursement-requests',
        label: 'Disbursements',
        icon: Wallet,
        to: '/cash-division/requests',
        permission: 'disbursement-requests.read',
      },
      {
        key: 'cash-division-settings',
        label: 'Settings',
        icon: Settings,
        to: '/cash-division/settings',
        anyPermission: ['cash-catalogs.read'],
        divider: true,
      },
    ],
  },

  /*
    SELF SERVICE — what an employee raises for THEMSELVES rather than on behalf of a module
    they operate. Its audience is every employee, where Logistics' is the supply-chain
    operators, which is why it is a top-level suite rather than a group inside Inventory.

    **It sits LAST among the real suites, and that is deliberate.** `firstVisibleRoute` walks
    these sections in order, so putting it first would land every administrator on a
    requisition register. Last, an employee still lands here because it is all they can see.

    **The `Inventory` group MIRRORS Logistics › Inventory and must keep its own `key`.** A
    node's key is also the localStorage key for its expanded state, so sharing
    `logistics-inventory` would make the two groups open and close as one. The source hit
    exactly this collision and documented it. Its OTHER hazard — a label-keyed suite map
    misrouting the section — does not transfer, because `suite` is named explicitly here.

    The group carries NO `permission`: it will hold entries from other modules as Self Service
    grows, and a key here would hide the branch from a reader entitled to one of them.
  */
  {
    suite: 'Self Service',
    items: [
      {
        key: 'self-service-inventory',
        label: 'Inventory',
        icon: Boxes,
        children: [
          {
            /*
              The requester's own slips. `requisition-slips.read` — the register's own key.
              The picker feeds behind the form carry `create` instead, because they disclose
              the whole item catalog and what is on hand of each.
            */
            key: 'requisition-slips',
            label: 'Requisition and Issue Slip',
            icon: ClipboardList,
            to: '/self-service/requisition-slips',
            permission: 'requisition-slips.read',
          },
        ],
      },
      {
        /*
      {
        /*
          EVIDENCE — product call, 09 Oct 2026: an employee asks a case's team to receive or transfer
          evidence. The group MIRRORS Evidence Management's and carries its OWN key (an expand key is a
          localStorage key — sharing `evidence-management` would open and close the two as one). NO
          `permission` on the group, for the Inventory group's reason.
        */
        key: 'self-service-evidence',
        label: 'Evidence',
        icon: ShieldCheck,
        children: [
          {
            // The requester's own requests. The case and item pickers carry `create`, behind the case's own access.
            key: 'evidence-requests',
            label: 'Evidence Request',
            icon: FileInput,
            to: '/self-service/evidence-requests',
            permission: 'evidence-requests.read',
          },
        ],
      },
      {
        /*
          MIRRORS the Cash Division suite, with its own `key` for the reason the Inventory group
          above has one: a node's key is its localStorage expand key, and sharing one would make
          the two open and close as one.
        */
        key: 'self-service-cash-division',
        label: 'Cash Division',
        icon: Wallet,
        children: [
          {
            /*
              The employee's own requests — `my-disbursement-requests.read`, a key set of its
              own. The Cash Division's `disbursement-requests.*` shows every request and its
              `create` implies the Personnel module; this one shows only the reader's.
            */
            key: 'my-disbursement-requests',
            label: 'Requests',
            icon: Wallet,
            to: '/self-service/disbursement-requests',
            permission: 'my-disbursement-requests.read',
          },
        ],
      },
      {
        /*
          MIRRORS the Learning & Development suite, with its OWN `key` for the reason Inventory's
          has one: a node's key is its localStorage expand key. No `permission` on the group — it
          will hold other modules' entries as Self Service grows.
        */
        key: 'self-service-learning-development',
        label: 'Learning & Development',
        icon: GraduationCap,
        children: [
          {
            // The surveys that cover the reader — `my-surveys.read`, never the administrator's
            // `surveys.read`, which reads every employee's response.
            key: 'my-surveys',
            label: 'Survey',
            icon: ListChecks,
            to: '/self-service/surveys',
            permission: 'my-surveys.read',
          },
          {
            // Every training recommendation sent to the reader, across every survey — its own
            // module and its own read, never `training-recommendations.read`, which reads every
            // employee's track. Its path sits beside Survey's, not under it: the rail draws its
            // active bar by prefix.
            key: 'my-training-recommendations',
            label: 'Training Recommendations',
            icon: GraduationCap,
            to: '/self-service/training-recommendations',
            permission: 'my-training-recommendations.read',
          },
        ],
      },
    ],
  },

  {
    /*
      ADMINISTRATION — LAST, below every suite that does the organization's work. These are the
      controls over the product itself: who holds an account, what they may do, what they may
      see, and what has been done. A reader opens them occasionally and deliberately, where the
      suites above are daily work — so the rail leads with the work and ends with the controls.
    */
    suite: 'Administration',
    items: [
      { key: 'users', label: 'Users', icon: Users, to: '/users', permission: 'users.read' },
      { key: 'roles', label: 'Roles', icon: Shield, to: '/roles', permission: 'roles.read' },
      {
        /*
          THE CLEARANCE LADDER — a rail row beside Roles, because it is the third thing that
          decides what a reader can see and it has a page of its own. It is not a settings list
          behind another module's cog: nothing here belongs to one module, and Personnel's own
          Settings carries only the roster of who sits where.

          Its path is NOT nested under `/users`, deliberately: the rail draws its active bar by
          prefix, and a route at `/users/access-levels` would light the Users row too.
        */
        key: 'access-levels',
        label: 'Access Levels',
        icon: ShieldCheck,
        to: '/access-levels',
        permission: 'access-levels.read',
      },
      { key: 'files', label: 'Files', icon: Paperclip, to: '/files', permission: 'files.read' },
      {
        key: 'audit-logs',
        label: 'Audit Logs',
        icon: ScrollText,
        to: '/audit-logs',
        permission: 'audit-logs.read',
      },
    ],
  },

  {
    suite: 'Sample — nesting demo',
    items: [
      {
        key: 'inventory',
        label: 'Sample Module',
        icon: Boxes,
        sample: true,
        children: [
          {
            key: 'inventory-stock',
            label: 'Stock Control',
            icon: Package,
            sample: true,
            children: [
              { key: 'inventory-stock-counts', label: 'Stock Counts', icon: ClipboardList, sample: true },
              { key: 'inventory-stock-adjust', label: 'Adjustments', icon: Layers, sample: true },
            ],
          },
          {
            key: 'inventory-catalog',
            label: 'Catalog',
            icon: FileText,
            sample: true,
            children: [
              { key: 'inventory-catalog-items', label: 'Items', sample: true },
              { key: 'inventory-catalog-units', label: 'Units of Measure', sample: true },
            ],
          },
        ],
      },
    ],
  },
];
